import type { ComponentType } from 'react';
import {
  IconAt,
  IconBook,
  IconCircleCheck,
  IconCircleExclamation,
  IconCirclePause,
  IconComment,
  IconFaceSmile,
  IconPenToSquare,
  IconReply,
  IconShareNodes,
  IconTriangleExclamation,
  IconUserPlus,
} from '@optiaxiom/icons';

import { buildMessagesPath } from '@frontend/utils/messagingUrl';

import type { NotificationCategory, NotificationPriority, NotificationType } from '../types';

/**
 * Inputs that can describe a notification — covers both the API model
 * (`Notification`) and the websocket payload (`NotificationCreatedData`).
 */
type NotificationLike = {
  id?: string;
  type: NotificationType | string;
  agent_id?: string | null;
  metadata?: {
    thread_id?: string;
    conversation_id?: string;
    message_id?: string;
    execution_id?: string;
    agent_type?: 'specialized_agent' | 'workflow_agent';
    trigger_type?: string;
    trigger_id?: string | null;
    agent_name?: string;
    modified_by?: string;
    skill_id?: string;
    skill_name?: string;
    resource_type?: 'specialized_agent' | 'workflow_agent' | 'space';
    resource_id?: string;
    element_id?: string | null;
    hitl?: {
      flow?: string;
      task_id?: string;
      step_id?: string;
      sub_step_id?: string;
      comment_id?: string;
    };
    slug?: string;
  } | null;
};

type DeepLinkBuilder = (notification: NotificationLike) => string;

/**
 * Toast/Indicator intents in Axiom. Notifications fall back to `information`
 * when a type isn't registered.
 */
export type NotificationIntent = 'success' | 'danger' | 'information';

/**
 * The single source of truth for everything a notification type drives in the
 * UI: which icon and color it uses, what toast intent fires, how it deep-links,
 * and its priority/category metadata. Add a new notification type here and
 * every consumer (tray, table cell, toast, badge) picks it up automatically.
 */
type NotificationVisuals = {
  Icon: ComponentType;
  color: 'fg.error' | 'fg.success' | 'fg.default' | 'fg.warning';
  intent: NotificationIntent;
  priority: NotificationPriority;
  category: NotificationCategory;
  buildDeepLinkPath: DeepLinkBuilder;
};

const chatThreadLink: DeepLinkBuilder = notification =>
  `/chat?threadId=${notification.metadata?.thread_id ?? ''}`;

const workflowExecutionLink: DeepLinkBuilder = notification =>
  `/agents/workflow/executions/${notification.metadata?.execution_id ?? ''}`;

const specializedAgentExecutionLink: DeepLinkBuilder = notification =>
  `/agents/specialized/${notification.agent_id ?? ''}?tab=execution&executionId=${notification.metadata?.execution_id ?? ''}`;

const messagingConversationLink: DeepLinkBuilder = notification => {
  const meta = notification.metadata as {
    conversation_id?: string;
    message_id?: string;
    thread_id?: string;
  } | null;
  return buildMessagesPath({
    conversationId: meta?.conversation_id,
    threadPanelId: meta?.thread_id,
    targetMessageId: meta?.message_id,
  });
};

const agentLink: DeepLinkBuilder = notification =>
  `/agents/by-agent-id/${notification.agent_id ?? ''}`;

const agentListLink: DeepLinkBuilder = () => '/agents';

const skillEditLink: DeepLinkBuilder = notification =>
  `/skills/edit/${notification.metadata?.skill_id ?? ''}`;

const contextLink: DeepLinkBuilder = () => '/context';

const brandsLink: DeepLinkBuilder = () => '/context/brands';

const resourceSharedLink: DeepLinkBuilder = notification => {
  const meta = notification.metadata;
  const resourceType = meta?.resource_type;
  const resourceId = meta?.resource_id;
  if ((resourceType === 'specialized_agent' || resourceType === 'workflow_agent') && resourceId) {
    return `/agents/by-agent-id/${resourceId}`;
  }
  if (resourceType === 'space') {
    if (meta?.element_id && resourceId) {
      return `/artifacts?artifactId=${meta.element_id}&spaceId=${resourceId}`;
    }
    return '/artifacts';
  }
  return '/notifications';
};

const NOTIFICATION_VISUALS: Record<NotificationType, NotificationVisuals> = {
  chat_agent_execution_completed: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Agent Execution',
    buildDeepLinkPath: chatThreadLink,
  },
  chat_agent_execution_failed_self: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Agent Execution',
    buildDeepLinkPath: chatThreadLink,
  },
  chat_agent_execution_failed_watched: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Agent Execution',
    buildDeepLinkPath: notification =>
      notification.metadata?.agent_type === 'workflow_agent'
        ? workflowExecutionLink(notification)
        : specializedAgentExecutionLink(notification),
  },
  autonomous_workflow_execution_completed: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Agent Execution',
    buildDeepLinkPath: workflowExecutionLink,
  },
  autonomous_workflow_execution_failed: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Agent Execution',
    buildDeepLinkPath: workflowExecutionLink,
  },
  agent_modified: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Agent Updates',
    buildDeepLinkPath: agentLink,
  },
  agent_activated: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'High',
    category: 'Agent Updates',
    buildDeepLinkPath: agentLink,
  },
  agent_deactivated: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Agent Updates',
    buildDeepLinkPath: agentLink,
  },
  agent_deleted: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Agent Updates',
    buildDeepLinkPath: agentListLink,
  },
  guardrail_warning: {
    Icon: IconTriangleExclamation,
    color: 'fg.warning',
    intent: 'information',
    priority: 'Medium',
    category: 'Guardrails',
    buildDeepLinkPath: specializedAgentExecutionLink,
  },
  guardrail_failed: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Guardrails',
    buildDeepLinkPath: specializedAgentExecutionLink,
  },
  org_skill_created: {
    Icon: IconBook,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Skill Updates',
    buildDeepLinkPath: skillEditLink,
  },
  org_skill_updated: {
    Icon: IconPenToSquare,
    color: 'fg.default',
    intent: 'information',
    priority: 'Low',
    category: 'Skill Updates',
    buildDeepLinkPath: skillEditLink,
  },
  org_skill_deleted: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Skill Updates',
    buildDeepLinkPath: contextLink,
  },
  brand_skill_compiled: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Skill Updates',
    buildDeepLinkPath: skillEditLink,
  },
  brand_skill_compile_failed: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Skill Updates',
    buildDeepLinkPath: brandsLink,
  },
  messaging_new_message: {
    Icon: IconComment,
    color: 'fg.default',
    intent: 'information',
    priority: 'Low',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_mention: {
    Icon: IconAt,
    color: 'fg.default',
    intent: 'information',
    priority: 'High',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_reply: {
    Icon: IconReply,
    color: 'fg.default',
    intent: 'information',
    priority: 'Low',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_reaction: {
    Icon: IconFaceSmile,
    color: 'fg.default',
    intent: 'information',
    priority: 'Low',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_channel_invite: {
    Icon: IconUserPlus,
    color: 'fg.success',
    intent: 'success',
    priority: 'High',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_group_invite: {
    Icon: IconUserPlus,
    color: 'fg.success',
    intent: 'success',
    priority: 'High',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  resource_shared: {
    Icon: IconShareNodes,
    color: 'fg.default',
    intent: 'information',
    priority: 'Low',
    category: 'Sharing',
    buildDeepLinkPath: resourceSharedLink,
  },
  share_approval_requested: {
    Icon: IconCirclePause,
    color: 'fg.warning',
    intent: 'information',
    priority: 'High',
    category: 'Sharing',
    buildDeepLinkPath: resourceSharedLink,
  },
  share_request_approved: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Sharing',
    buildDeepLinkPath: resourceSharedLink,
  },
  share_request_rejected: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'Medium',
    category: 'Sharing',
    buildDeepLinkPath: resourceSharedLink,
  },
  unshare_approval_requested: {
    Icon: IconCirclePause,
    color: 'fg.warning',
    intent: 'information',
    priority: 'High',
    category: 'Sharing',
    buildDeepLinkPath: resourceSharedLink,
  },
  unshare_request_approved: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Sharing',
    buildDeepLinkPath: resourceSharedLink,
  },
  unshare_request_rejected: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'Medium',
    category: 'Sharing',
    buildDeepLinkPath: resourceSharedLink,
  },
  workflow_execution_hitl_requested: {
    Icon: IconCirclePause,
    color: 'fg.default',
    intent: 'information',
    priority: 'High',
    category: 'Agent Execution',
    buildDeepLinkPath: () => '/notifications',
  },
  teammate_enable_requested: {
    Icon: IconUserPlus,
    color: 'fg.default',
    intent: 'information',
    priority: 'High',
    category: 'Teammate Directory',
    buildDeepLinkPath: notification =>
      notification.metadata?.slug
        ? `/vaus/directory/${notification.metadata.slug}`
        : '/notifications',
  },
};

const FALLBACK_DEEP_LINK_PATH = '/notifications';

export function getNotificationVisuals(
  type: NotificationType | string
): NotificationVisuals | undefined {
  return NOTIFICATION_VISUALS[type as NotificationType];
}

export function getPriority(type: NotificationType): NotificationPriority {
  return NOTIFICATION_VISUALS[type]?.priority ?? 'Low';
}

export function getCategory(type: NotificationType): NotificationCategory {
  return NOTIFICATION_VISUALS[type]?.category ?? 'Agent Execution';
}

export function getNotificationIntent(type: NotificationType | string): NotificationIntent {
  return NOTIFICATION_VISUALS[type as NotificationType]?.intent ?? 'information';
}

/** Badge intent used to render a priority pill (handles both UI labels and backend keys). */
export type PriorityBadgeIntent = 'danger' | 'warning' | 'information' | 'neutral';

export function getPriorityIntent(priority: string | undefined | null): PriorityBadgeIntent {
  switch (priority?.toLowerCase()) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'information';
    case 'low':
    default:
      return 'neutral';
  }
}

/**
 * Build the in-app deep-link target for a notification, relative to the
 * current instance namespace (use with `useNamespacedNavigate` or prefix
 * with `/instances/{instanceId}` for an `<a href>`).
 *
 * Falls back to the notification center when the type isn't registered.
 */
export function getDeepLinkPath(notification: NotificationLike): string {
  return (
    NOTIFICATION_VISUALS[notification.type as NotificationType]?.buildDeepLinkPath(notification) ??
    FALLBACK_DEEP_LINK_PATH
  );
}

/**
 * Formats an unread count for display in a badge or tab label.
 * - 0 / undefined → ''
 * - 1–99 → the number
 * - 100+ → '99+'
 */
export function formatUnreadCount(count: number | undefined): string {
  if (!count || count <= 0) return '';
  return count > 99 ? '99+' : String(count);
}

// --- Filter options (shared by NotificationFilterBar) ---

/** Backend category_key values (matches `CATEGORY_TYPE_MAP` in notification-service registry). */
export type NotificationCategoryFilter =
  | 'agent_execution'
  | 'agent_updates'
  | 'guardrail'
  | 'skill_updates'
  | 'messaging'
  | 'sharing'
  | 'teammate_directory';

/** Backend priority values (matches `PRIORITY_TYPE_MAP` in notification-service registry). */
export type NotificationPriorityFilter = 'critical' | 'high' | 'medium' | 'low';

export const CATEGORY_OPTIONS: ReadonlyArray<{
  label: string;
  value: NotificationCategoryFilter;
}> = [
  { label: 'Agent Updates', value: 'agent_updates' },
  { label: 'Agent Execution', value: 'agent_execution' },
  { label: 'Guardrails', value: 'guardrail' },
  { label: 'Skill Updates', value: 'skill_updates' },
  { label: 'Messaging', value: 'messaging' },
  { label: 'Sharing', value: 'sharing' },
  { label: 'Teammate Directory', value: 'teammate_directory' },
];

export const PRIORITY_OPTIONS: ReadonlyArray<{
  label: string;
  value: NotificationPriorityFilter;
}> = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

export type TimePreset = 'last_hour' | 'last_24_hours' | 'last_7_days' | 'last_30_days';

export const TIME_PRESETS: ReadonlyArray<{ label: string; value: TimePreset }> = [
  { label: 'Last Hour', value: 'last_hour' },
  { label: 'Last 24 Hours', value: 'last_24_hours' },
  { label: 'Last 7 Days', value: 'last_7_days' },
  { label: 'Last 30 Days', value: 'last_30_days' },
];

const TIME_PRESET_MS: Record<TimePreset, number> = {
  last_hour: 3_600_000,
  last_24_hours: 86_400_000,
  last_7_days: 7 * 86_400_000,
  last_30_days: 30 * 86_400_000,
};

/**
 * Type guard: narrows `string | null | undefined` to `TimePreset` if the
 * value matches a known preset.
 */
export function isTimePreset(value: string | null | undefined): value is TimePreset {
  return value !== null && value !== undefined && value in TIME_PRESET_MS;
}

/**
 * Converts a time preset to the ISO datetime to send as `created_after`.
 * Each preset is a sliding window relative to `now`.
 */
export function timePresetToCreatedAfter(preset: TimePreset, now: Date = new Date()): string {
  return new Date(now.getTime() - TIME_PRESET_MS[preset]).toISOString();
}

export function formatTimestamp(date: Date): { time: string; date: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (dateStart.getTime() === today.getTime()) {
    return { time, date: 'Today' };
  }
  if (dateStart.getTime() === yesterday.getTime()) {
    return { time, date: 'Yesterday' };
  }
  return {
    time,
    date: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  };
}

export type HitlTaskDialogState = {
  taskId: string;
  /** The Proteus card to render. Empty means there is nothing to answer. */
  commentId: string;
  agentName?: string;
  /**
   * Recipient id of the notification that opened the dialog, so approving or
   * declining can flip that notification's action state to completed.
   */
  notificationId?: string;
} | null;

export function isHitlNotification(notification: NotificationLike): boolean {
  return (
    notification.type === 'workflow_execution_hitl_requested' &&
    !!notification.metadata?.hitl?.task_id
  );
}

/**
 * The dialog needs the task and its one card, and nothing else the `hitl` block
 * carries — `step_id` / `sub_step_id` are on the wire for a client that has to act
 * on the sub-step, which this one does not. They stay off the view model until
 * something renders from them.
 */
export function toHitlDialogState(notification: NotificationLike): HitlTaskDialogState {
  const hitl = notification.metadata?.hitl;
  if (!isHitlNotification(notification) || !hitl?.task_id) return null;
  return {
    taskId: hitl.task_id,
    commentId: hitl.comment_id || '',
    agentName: notification.metadata?.agent_name,
    notificationId: notification.id,
  };
}
